import React from 'react'
import { QuestionnaireItem } from '@medplum/fhirtypes'
import { useAssessmentContext } from 'contexts/AssessmentContext'
import { getNestedItems } from 'components/Assessments/helpers'
import { DEFAULT_REQUIRED_FIELD_MESSAGE } from 'components/Assessments/constants'
import { useQAAssessmentActionsContext } from 'contexts/QAAssessmentActionsContext'
import { useQAAssessmentCommentsContext } from 'contexts/QAAssessmentCommentsContext'
import { getCommentQuestionLinkId } from 'components/QA/Assessments/helpers'
import { BradenScaleFormField } from './formField'
import { BradenScaleQuestionnaireItem } from './questionnaire'
import { CodingRadioQuestionWithNotes } from '../../components'
import { PATSectionComponentProps } from '../../types/components'

export default function BradenScaleSection({
  item: section,
  sectionId: _sectionId,
  sectionIndex: _sectionIndex
}: PATSectionComponentProps): JSX.Element {
  const {
    cantEditAssessmentFields,
    assessmentMustBeFinalized,
    assessmentStatusState: { inQA, inQAEdit }
  } = useAssessmentContext()
  const { createCommentClickHandler, disableHover } = useQAAssessmentActionsContext()
  const { sortedComments: comments, focusedCommentId } = useQAAssessmentCommentsContext()
  const focusedComment = comments.find((c) => c.id === focusedCommentId)
  const focusedCommentLinkId = focusedComment ? getCommentQuestionLinkId(focusedComment) : null

  // Helper function to create comment click handler for a question field
  const createQuestionCommentClickHandler = (field: QuestionnaireItem | undefined) => (): void => {
    if (field?.linkId) {
      createCommentClickHandler(field.linkId)
    }
  }

  // Deterministic mapping of linkId to JSX renderer function.
  const fieldComponentMap: Record<
    string,
    (question: QuestionnaireItem, notes: QuestionnaireItem) => JSX.Element
  > = {
    [BradenScaleQuestionnaireItem.BRADEN_SCALE_SENSORY_QUESTION]: (question, notes) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={BradenScaleFormField.BRADEN_SCALE_SENSORY_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={BradenScaleFormField.BRADEN_SCALE_SENSORY_NOTES}
        disabled={cantEditAssessmentFields}
        customRules={[
          {
            required: Boolean(assessmentMustBeFinalized && question.required),
            message: DEFAULT_REQUIRED_FIELD_MESSAGE
          }
        ]}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
      />
    ),
    [BradenScaleQuestionnaireItem.BRADEN_SCALE_MOISTURE_QUESTION]: (question, notes) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={BradenScaleFormField.BRADEN_SCALE_MOISTURE_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={BradenScaleFormField.BRADEN_SCALE_MOISTURE_NOTES}
        disabled={cantEditAssessmentFields}
        customRules={[
          {
            required: Boolean(assessmentMustBeFinalized && question.required),
            message: DEFAULT_REQUIRED_FIELD_MESSAGE
          }
        ]}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
      />
    ),
    [BradenScaleQuestionnaireItem.BRADEN_SCALE_ACTIVITY_QUESTION]: (question, notes) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={BradenScaleFormField.BRADEN_SCALE_ACTIVITY_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={BradenScaleFormField.BRADEN_SCALE_ACTIVITY_NOTES}
        disabled={cantEditAssessmentFields}
        customRules={[
          {
            required: Boolean(assessmentMustBeFinalized && question.required),
            message: DEFAULT_REQUIRED_FIELD_MESSAGE
          }
        ]}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
      />
    ),
    [BradenScaleQuestionnaireItem.BRADEN_SCALE_MOBILITY_QUESTION]: (question, notes) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={BradenScaleFormField.BRADEN_SCALE_MOBILITY_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={BradenScaleFormField.BRADEN_SCALE_MOBILITY_NOTES}
        disabled={cantEditAssessmentFields}
        customRules={[
          {
            required: Boolean(assessmentMustBeFinalized && question.required),
            message: DEFAULT_REQUIRED_FIELD_MESSAGE
          }
        ]}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
      />
    ),
    [BradenScaleQuestionnaireItem.BRADEN_SCALE_NUTRITION_QUESTION]: (question, notes) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={BradenScaleFormField.BRADEN_SCALE_NUTRITION_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={BradenScaleFormField.BRADEN_SCALE_NUTRITION_NOTES}
        disabled={cantEditAssessmentFields}
        customRules={[
          {
            required: Boolean(assessmentMustBeFinalized && question.required),
            message: DEFAULT_REQUIRED_FIELD_MESSAGE
          }
        ]}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
      />
    ),
    [BradenScaleQuestionnaireItem.BRADEN_SCALE_FRICTION_AND_SHEAR_QUESTION]: (question, notes) => (
      <CodingRadioQuestionWithNotes
        key={question.linkId}
        field={question}
        formFieldName={BradenScaleFormField.BRADEN_SCALE_FRICTION_AND_SHEAR_QUESTION}
        notesField={notes.linkId ? notes : undefined}
        notesFormFieldName={BradenScaleFormField.BRADEN_SCALE_FRICTION_AND_SHEAR_NOTES}
        disabled={cantEditAssessmentFields}
        customRules={[
          {
            required: Boolean(assessmentMustBeFinalized && question.required),
            message: DEFAULT_REQUIRED_FIELD_MESSAGE
          }
        ]}
        inQA={inQA}
        inQAEdit={inQAEdit}
        disableHover={disableHover}
        isFocused={question.linkId === focusedCommentLinkId}
        onCommentClick={createQuestionCommentClickHandler(question)}
      />
    )
  }

  // Iterate over section.items in questionnaire order
  const questionGroups =
    section?.item && Array.isArray(section.item)
      ? section.item.filter(
          (item): item is QuestionnaireItem => item.type === 'group' && !!item.item
        )
      : []

  // Guard clause: return early if section is undefined, null, or has no items
  if (!section || !section.item || !Array.isArray(section.item) || questionGroups.length === 0) {
    return <></>
  }

  // Render questions in questionnaire order
  return (
    <>
      {questionGroups.map((questionGroup) => {
        const { question, notes } = getNestedItems(questionGroup.item || [])

        if (!question.linkId) {
          return null
        }

        const renderField = fieldComponentMap[question.linkId]

        if (!renderField) {
          return null
        }

        return renderField(question, notes)
      })}
    </>
  )
}
